import { apiClient } from '@/modules/shared/services/apiClient';
import type {
  ProgramAuthSettings,
  UpsertProgramAuthSettingsRequest,
  ProgramAuthMode,
} from '../types/programAuthSettings';

interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  message: string;
  errors: string[];
  correlationId: string;
}

export const programAuthSettingsService = {
  /**
   * Get program-level auth settings (admin-only).
   */
  async getByProgramId(programId: string): Promise<ApiResponse<ProgramAuthSettings>> {
    const response = await apiClient.get<ApiResponse<ProgramAuthSettings>>(
      `/programs/${programId}/auth-settings`
    );
    return response.data;
  },

  /**
   * Save program-level auth settings (create or update).
   */
  async upsert(
    programId: string,
    data: UpsertProgramAuthSettingsRequest
  ): Promise<ApiResponse<ProgramAuthSettings>> {
    const response = await apiClient.put<ApiResponse<ProgramAuthSettings>>(
      `/programs/${programId}/auth-settings`,
      data
    );
    return response.data;
  },
};

/**
 * Public endpoint — no auth required.
 * Returns the effective auth mode for a program (for the login page).
 */
export async function getProgramAuthMode(params: {
  programCode?: string;
  programId?: string;
}): Promise<ApiResponse<ProgramAuthMode>> {
  const query = new URLSearchParams();
  if (params.programCode) query.set('programCode', params.programCode);
  if (params.programId) query.set('programId', params.programId);
  const response = await apiClient.get<ApiResponse<ProgramAuthMode>>(
    `/auth/program-auth-mode?${query.toString()}`
  );
  return response.data;
}
